<div class="flex flex-col min-h-screen mt-4 px-4">
  <div class="flex items-center justify-center">
    <p-fluid>
      <div class="flex items-center justify-center w-full">
        <div class="w-full max-w-4xl rounded-xl">
          <div class="flex gap-3 items-center mb-6">
            <p-button
              size="large"
              icon="pi pi-arrow-left"
              text
              severity="contrast"
              (onClick)="goBack()"
            ></p-button>
          </div>
          <form [formGroup]="form" (ngSubmit)="onSubmit()" class="flex flex-col gap-4">
            <div class="card flex flex-col gap-4">
              <div class="font-semibold text-2xl">
                {{ 'account.register.selectPaymentPlan' | transloco }}
              </div>
              <div class="flex flex-col gap-2">
                <label for="selectedPlan">
                  {{ 'common.paymentPlan' | transloco }}
                  <span>*</span>
                </label>
                <app-payment-plan-select
                  id="selectedPlan"
                  formControlName="selectedPlan"
                  [placeholder]="'common.selectPlan' | transloco"
                  (plansLoaded)="onPaymentPlansLoaded($event)"
                  [ngClass]="{
                    'ng-invalid ng-dirty':
                      form.get('selectedPlan')?.invalid && form.get('selectedPlan')?.touched,
                  }"
                ></app-payment-plan-select>
                @if (form.get('selectedPlan')?.touched && form.get('selectedPlan')?.invalid) {
                  <p-message
                    severity="error"
                    variant="simple"
                    size="small"
                    data-testid="payment-plan-error"
                  >
                    {{ 'account.validation.requiredField' | transloco }}
                  </p-message>
                }
              </div>
            </div>

            @if (form.get('selectedPlan')?.value) {
              <div class="card flex flex-col gap-4" data-testid="module-function-card">
                <ng-container *transloco="let t; prefix: 'account.register'">
                  <div class="font-semibold text-2xl">{{ t('selectModuleFunctions') }}</div>

                  @if (isIndividualPlan()) {
                    <div class="p-4 bg-third-level rounded-lg" data-testid="individual-plan-info">
                      <div class="flex items-start gap-3">
                        <i class="pi pi-info-circle text-xl"></i>
                        <div>{{ t('individualPlanInfo') }}</div>
                      </div>
                    </div>
                  } @else {
                    <div>{{ t('chooseModuleFunctions') }}</div>

                    <div class="flex items-center gap-4">
                      <button
                        type="button"
                        class="flex items-center justify-center rounded-full bg-primary w-10 h-10"
                        (click)="openModuleFunctionDialog()"
                        data-testid="open-functions-dialog"
                      >
                        <i class="pi pi-plus" style="font-size: 1rem; font-weight: bold"></i>
                      </button>

                      <div class="grow">
                        @if (selectedModuleFunctions().length > 0) {
                          <div class="flex flex-wrap gap-2" data-testid="selected-function-badges">
                            @for (
                              moduleInfo of selectedFunctionsDisplay();
                              track moduleInfo.moduleName
                            ) {
                              <span
                                [pTooltip]="tooltipTemplate"
                                [tooltipStyleClass]="'large-tooltip'"
                                tooltipPosition="top"
                                class="px-3 py-1 bg-third-level rounded-lg text-sm cursor-pointer hover:shadow-sm transition-all"
                                data-testid="function-badge"
                              >
                                {{ moduleInfo.moduleName }} ({{ moduleInfo.count }})

                                <ng-template #tooltipTemplate>
                                  <div class="custom-tooltip-content">
                                    {{ moduleInfo.functionNames.join(', ') }}
                                  </div>
                                </ng-template>
                              </span>
                            }
                          </div>
                        } @else {
                          <span class="text-gray-500 text-sm" data-testid="no-functions-selected">{{
                            'account.register.noFunctionsSelected' | transloco
                          }}</span>
                          <p-message
                            severity="error"
                            variant="simple"
                            size="small"
                            data-testid="payment-plan-selected-module-error"
                          >
                            {{ 'account.validation.requiredField' | transloco }}
                          </p-message>
                        }
                      </div>
                    </div>
                  }
                </ng-container>
              </div>
            }

            <div class="card flex flex-col gap-4">
              <ng-container *transloco="let t; prefix: 'account.register'">
                <div class="font-semibold text-2xl">{{ t('personalData') }}</div>
                {{ t('enterPersonalData') }}
              </ng-container>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <app-form-field
                  id="firstName"
                  fieldName="firstName"
                  label="common.firstName"
                ></app-form-field>

                <app-form-field
                  id="lastName"
                  fieldName="lastName"
                  label="common.lastName"
                ></app-form-field>
              </div>

              <app-form-field
                id="tenantName"
                fieldName="tenantName"
                label="common.companyName"
              ></app-form-field>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <app-form-field
                  id="phoneNumber"
                  fieldName="phoneNumber"
                  label="common.phoneNumber"
                  type="tel"
                  placeholder="+389..."
                ></app-form-field>

                <app-form-field
                  id="email"
                  fieldName="email"
                  label="common.email"
                  type="email"
                  placeholder="beispiel@email.com"
                ></app-form-field>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <app-form-field
                  id="password"
                  fieldName="password"
                  label="common.password"
                  type="password"
                ></app-form-field>

                <app-form-field
                  id="passwordRepeat"
                  fieldName="passwordRepeat"
                  label="account.register.passwordRepeat"
                  type="passwordRepeat"
                ></app-form-field>
              </div>
            </div>

            <!-- Card 2: Billing Data -->
            <div class="card flex flex-col gap-4">
              <ng-container *transloco="let t; prefix: 'account.register'">
                <div class="font-semibold text-2xl">{{ t('billingInformation') }}</div>
              </ng-container>

              <app-billing-form></app-billing-form>

              <div class="flex flex-col gap-2 mt-4">
                <div class="flex items-start gap-2">
                  <p-checkbox
                    formControlName="acceptTerms"
                    [binary]="true"
                    inputId="acceptTerms"
                    data-testid="accept-terms-checkbox"
                  ></p-checkbox>
                  <label for="acceptTerms" class="text-sm uppercase font-bold">
                    <div class="mb-2">
                      {{ 'account.register.acceptTermsPrefix' | transloco }}
                      @if (termsOfUseLink) {
                        <a
                          [href]="termsOfUseLink.url"
                          appLinkTabOpener
                          class="text-red-500 font-bold"
                          data-testid="terms-of-use-link"
                        >
                          {{ termsOfUseLink.translation_key | transloco }}
                        </a>
                      }
                      {{ 'account.register.acceptTermsSuffix' | transloco }}
                    </div>
                    <div>
                      {{ 'account.register.privacyPolicyPrefix' | transloco }}
                      @if (privacyPolicyLink) {
                        <a
                          [href]="privacyPolicyLink.url"
                          appLinkTabOpener
                          class="text-red-500 font-bold"
                          data-testid="privacy-policy-link"
                        >
                          {{ privacyPolicyLink.translation_key | transloco }}
                        </a>
                      }
                      {{ 'account.register.privacyPolicySuffix' | transloco }}
                    </div>
                  </label>
                </div>
                @if (form.get('acceptTerms')?.touched && form.get('acceptTerms')?.invalid) {
                  <p-message
                    severity="error"
                    variant="simple"
                    size="small"
                    data-testid="accept-terms-error"
                  >
                    {{ 'account.register.mustAcceptTerms' | transloco }}
                  </p-message>
                }
              </div>

              <div class="mt-4">
                <p-button
                  data-testid="register-button"
                  type="submit"
                  [label]="'common.register' | transloco"
                  [loading]="submitting"
                  [disabled]="
                    form.invalid ||
                    billingForm().form.invalid ||
                    selectedModuleFunctions().length === 0
                  "
                ></p-button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </p-fluid>
  </div>

  <app-function-selection-dialog
    [(visible)]="moduleFunctionDialogVisible"
    (selectedFunctions)="onModuleFunctionsSelected($event)"
    [initialSelectedFunctions]="selectedModuleFunctions()"
    [maxSelections]="maxFunctions()"
    [allowCrossModule]="allowCrossModule()"
    [translationPrefix]="moduleFunctionDialogTranslationPrefix"
  ></app-function-selection-dialog>
</div>
